import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, X, FileText, Check, ChevronsUpDown, Loader2, Search, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import api from "../../utils/api";
import { toast } from "sonner";
import { cn } from "../../components/ui/utils";

interface Employee {
  id: number;
  name: string;
  jobRole: string;
  standardDailyHours?: number;
  department?: {
    departmentName: string;
  };
}

const getLeaveTypeLabel = (type: string) => {
  if (type === "Personal Leave") return "Personal/Sick/Carers Leave";
  return type;
};

const ReportAbsence = () => {
  const navigate = useNavigate();
  const [subordinates, setSubordinates] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showInsufficientBalanceDialog, setShowInsufficientBalanceDialog] = useState(false);

  // Form state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [reportTime, setReportTime] = useState<string>(
    new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
  );
  const [leaveType, setLeaveType] = useState<string>("Personal Leave");
  const [hoursType, setHoursType] = useState<"full" | "half" | "custom">("full");
  const [customHours, setCustomHours] = useState("8");
  const [reason, setReason] = useState("");
  const [publicHolidays, setPublicHolidays] = useState<any[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<Record<string, { quota: number; used: number; remaining: number }>>({});

  // Calculate total hours based on date range and daily hours (excluding weekends & public holidays)
  const calculateWorkingDays = (start: string, end: string, holidays: any[]): { workingDays: number; holidayCount: number } => {
    if (!start || !end) return { workingDays: 0, holidayCount: 0 };
    const s = new Date(start);
    const e = new Date(end);
    if (s > e) return { workingDays: 0, holidayCount: 0 };
    let workingCount = 0;
    let holidayCount = 0;
    const current = new Date(s);
    while (current <= e) {
      const day = current.getDay();
      const isWeekend = day === 0 || day === 6;

      const isHoliday = holidays.some(ph => {
        if (!ph.doNotDeduct) return false;
        const phStart = new Date(ph.startDate);
        const phEnd = new Date(ph.endDate);
        const checkDate = new Date(current.getFullYear(), current.getMonth(), current.getDate());
        const phS = new Date(phStart.getFullYear(), phStart.getMonth(), phStart.getDate());
        const phE = new Date(phEnd.getFullYear(), phEnd.getMonth(), phEnd.getDate());
        return checkDate >= phS && checkDate <= phE;
      });

      if (isHoliday) {
        holidayCount++;
      } else if (!isWeekend) {
        workingCount++;
      }
      current.setDate(current.getDate() + 1);
    }
    return { workingDays: workingCount, holidayCount };
  };

  const { workingDays, holidayCount } = calculateWorkingDays(startDate, endDate, publicHolidays);
  const totalHours = workingDays * parseFloat(customHours || "0");

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchSubordinates = async () => {
      try {
        const response = await api.get("/api/attendance/subordinates");
        if (response.success) {
          setSubordinates(response.data);
        }
      } catch (error) {
        console.error("Error fetching subordinates:", error);
        toast.error("Failed to load team members");
      } finally {
        setLoadingEmployees(false);
      }
    };

    const fetchHolidays = async () => {
      try {
        const res = await api.get('/api/attendance/public-holidays');
        if (res.success && res.data) {
          setPublicHolidays(res.data);
        }
      } catch (error) {
        console.error("Error fetching public holidays:", error);
      }
    };

    fetchSubordinates();
    fetchHolidays();
  }, []);

  const selectedEmployee = subordinates.find(s => s.id.toString() === selectedEmployeeId);
  const employeeDailyHours = parseFloat(String(selectedEmployee?.standardDailyHours ?? 8));

  // Fetch leave balances for the selected employee
  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const res = await api.get(`/api/attendance/leave-balance?employeeId=${selectedEmployeeId}`);
        if (res.success && res.data) {
          setLeaveBalances(res.data);
        }
      } catch (error) {
        console.error("Error fetching leave balances:", error);
      }
    };

    if (selectedEmployeeId) {
      fetchBalances();
    } else {
      setLeaveBalances({});
    }
  }, [selectedEmployeeId]);

  const handleHoursTypeChange = (val: "full" | "half" | "custom") => {
    setHoursType(val);
    if (val === "full") {
      setCustomHours(String(employeeDailyHours));
    } else if (val === "half") {
      setCustomHours(String(employeeDailyHours / 2));
    }
  };

  const handleCustomHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHoursType("custom");
    setCustomHours(e.target.value);
  };

  // Update custom hours when selected employee changes and a preset is active
  useEffect(() => {
    if (hoursType === "full") {
      setCustomHours(String(employeeDailyHours));
    } else if (hoursType === "half") {
      setCustomHours(String(employeeDailyHours / 2));
    }
  }, [selectedEmployeeId, employeeDailyHours]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      const validFiles = selected.filter(f => f.size <= 10 * 1024 * 1024);
      if (validFiles.length < selected.length) {
        toast.error("Some files were skipped as they exceed 10MB");
      }
      setAttachments(prev => [...prev, ...validFiles]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedEmployeeId) {
      toast.error("Please select an employee");
      return;
    }
    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }
    if (!endDate) {
      toast.error("Please select an end date");
      return;
    }
    if (!leaveType) {
      toast.error("Please select a leave type");
      return;
    }
    if (!customHours || parseFloat(customHours) <= 0) {
      toast.error("Please enter valid hours");
      return;
    }

    // Insufficient balance guard: block when the selected leave type tracks a
    // balance and the employee is already negative, or this report would project
    // the balance into the negative. "Unpaid Leave" has no tracked balance,
    // so it is never blocked.
    const selectedBalance = leaveBalances[leaveType];
    if (selectedBalance && (selectedBalance.remaining < 0 || selectedBalance.remaining - totalHours < 0)) {
      setShowInsufficientBalanceDialog(true);
      return;
    }

    setSubmitting(true);
    try {
      let uploadedAttachments = [];
      if (attachments.length > 0) {
        const uploadData = new FormData();
        attachments.forEach(file => uploadData.append('files', file));

        const uploadRes = await api.post('/api/attendance/leave-request/upload', uploadData);
        if (uploadRes.success && uploadRes.urls) {
          uploadedAttachments = uploadRes.urls;
        }
      }

      const response = await api.post("/api/attendance/report-absence", {
        employeeId: parseInt(selectedEmployeeId),
        startDate: startDate,
        endDate: endDate,
        leaveType,
        hours: customHours,
        reason,
        attachments: uploadedAttachments,
        reportTime
      });

      if (response.success) {
        toast.success("Absence reported successfully");
        navigate('/attendance');
      } else {
        toast.error(response.message || "Failed to report absence");
      }
    } catch (error: any) {
      if (error?.data?.code === 'INSUFFICIENT_LEAVE_BALANCE') {
        setShowInsufficientBalanceDialog(true);
      } else {
        console.error("Submit error:", error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          className="hover:bg-gray-100 flex items-center gap-2 text-gray-700 font-medium px-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={20} />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Report Employee Absence</h1>
          <p className="text-gray-500 font-medium text-sm">Record an absence for a team member who didn't come to work</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Form */}
        <div className="lg:col-span-2">
      <div className="bg-white p-8 rounded-[24px] border border-gray-100 shadow-sm space-y-6">
        {/* Employee Searchable Dropdown */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-900">
            Employee <span className="text-red-500">*</span>
          </label>
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={searchOpen}
                className="w-full justify-between rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all text-gray-900 font-normal"
                disabled={loadingEmployees}
              >
                {loadingEmployees ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading members...
                  </div>
                ) : selectedEmployee ? (
                  `${selectedEmployee.name} (${selectedEmployee.department?.departmentName || 'N/A'})`
                ) : (
                  "Select employee..."
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl border-gray-100 shadow-lg">
              <Command className="rounded-xl">
                <CommandInput
                  placeholder="Search employee..."
                  className="!border-none !ring-0 !ring-offset-0 focus:!ring-0 focus:!border-none focus-visible:!ring-0 shadow-none outline-none focus:outline-none"
                />
                <CommandEmpty>No employee found.</CommandEmpty>
                <CommandGroup className="max-h-[300px] overflow-auto">
                  {subordinates.map((emp) => (
                    <CommandItem
                      key={emp.id}
                      value={emp.name}
                      onSelect={() => {
                        setSelectedEmployeeId(emp.id.toString());
                        setSearchOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedEmployeeId === emp.id.toString() ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{emp.name}</span>
                        <span className="text-xs text-gray-500">{emp.jobRole} • {emp.department?.departmentName || 'N/A'}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-900">
              Start Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all text-gray-900 w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-900">
              End Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all text-gray-900 w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-900">
              Report Time <span className="text-red-500">*</span>
            </label>
            <Input
              type="time"
              value={reportTime}
              onChange={(e) => setReportTime(e.target.value)}
              className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all text-gray-900 w-full"
            />
          </div>
        </div>

        {/* Leave Type */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-900">
            Leave Type <span className="text-red-500">*</span>
          </label>
          <Select value={leaveType} onValueChange={setLeaveType}>
            <SelectTrigger className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all text-gray-900">
              <SelectValue placeholder="Select leave type..." />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-100">
              <SelectItem value="Annual Leave">Annual Leave</SelectItem>
              <SelectItem value="Personal Leave">Personal Leave</SelectItem>
              <SelectItem value="Unpaid Leave">Unpaid Leave</SelectItem>
            </SelectContent>
          </Select>

        </div>

        {/* Hours */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-900">
            Hours <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              className={cn(
                "flex-[2] py-3 rounded-xl border transition-all flex flex-col items-center justify-center",
                hoursType === "half"
                  ? "border-black bg-black text-white shadow-lg shadow-gray-200"
                  : "border-gray-100 bg-gray-50/50 hover:bg-gray-100/50 text-gray-500"
              )}
              onClick={() => handleHoursTypeChange("half")}
            >
              <span className="text-base font-bold">Half Day</span>
              <span className="text-xs opacity-80">{(employeeDailyHours / 2).toFixed(2)} hrs</span>
            </button>
            <button
              type="button"
              className={cn(
                "flex-[2] py-3 rounded-xl border transition-all flex flex-col items-center justify-center",
                hoursType === "full"
                  ? "border-black bg-black text-white shadow-lg shadow-gray-200"
                  : "border-gray-100 bg-gray-50/50 hover:bg-gray-100/50 text-gray-500"
              )}
              onClick={() => handleHoursTypeChange("full")}
            >
              <span className="text-base font-bold">Full Day</span>
              <span className="text-xs opacity-80">{employeeDailyHours.toFixed(2)} hrs</span>
            </button>
            <div className="flex-1">
              <Input
                type="number"
                min="0.5"
                step="0.5"
                max="24"
                placeholder="Hrs"
                value={customHours}
                onChange={handleCustomHoursChange}
                onFocus={() => handleHoursTypeChange("custom")}
                className={cn(
                  "rounded-xl border h-[55px] text-center text-lg font-bold transition-all",
                  hoursType === "custom"
                    ? "border-black bg-white ring-1 ring-black text-gray-900"
                    : "border-gray-100 bg-gray-50/50 text-gray-400"
                )}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-1">
            <p className="text-[10px] text-gray-400 font-medium ml-1">Select a preset or enter custom hours.</p>
            {workingDays > 0 && (
              <p className="text-xs font-bold text-gray-900">
                {workingDays} working day{workingDays !== 1 ? 's' : ''}
                {holidayCount > 0 && ` + ${holidayCount} public holiday${holidayCount !== 1 ? 's' : ''}`}
                {' '}× {customHours} hrs = <span className="text-blue-600">{totalHours.toFixed(1)} hrs total</span>
              </p>
            )}
          </div>
        </div>

        {/* Reason for Absence */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-900 text-left w-full block">Reason for Absence</label>
          <Textarea
            placeholder="Provide additional details about the absence..."
            className="rounded-xl border-gray-200 bg-gray-50/50 min-h-[120px] focus:bg-white transition-all text-gray-900 resize-y"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {/* Attachments Section Reused from RequestLeave */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-gray-900">Attachments</label>
            <span className="text-xs text-gray-500">{attachments.length} file(s) attached</span>
          </div>

          <div
            className="border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center bg-gray-50/30 hover:bg-gray-50 transition-all cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.jpg,.png"
            />
            <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 mb-3 group-hover:scale-110 transition-transform">
              <Upload className="h-6 w-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Click to browse or drag and drop files</p>
            <p className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
          </div>

          {attachments.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-xl group/item">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-100">
                      <FileText className="h-4 w-4 text-blue-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeAttachment(index); }}
                    className="p-1 hover:bg-red-50 hover:text-red-500 text-gray-400 rounded-md transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <hr className="border-gray-100 my-6" />

        {/* Actions */}
        <div className="flex justify-end items-center gap-3">
          <Button
            variant="outline"
            className="rounded-xl border-gray-200 bg-white hover:bg-gray-50 text-gray-700 h-11 px-6 font-semibold"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="rounded-xl bg-black text-white hover:bg-gray-800 h-11 px-6 font-semibold shadow-sm flex items-center gap-2"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Reporting...
              </>
            ) : (
              "Submit Absence Report"
            )}
          </Button>
        </div>
      </div>
        </div>

        {/* Right Column - Leave Balance */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <CardHeader className="bg-gray-50/50 border-b border-gray-50 pb-4">
              <CardTitle className="text-lg font-bold">
                {selectedEmployee ? `${selectedEmployee.name}'s Leave Balance` : "Leave Balance"}
              </CardTitle>
              <CardDescription>Current available leave hours</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {!selectedEmployee ? (
                <p className="text-sm text-gray-400 font-medium italic">
                  Select an employee to view their leave balance.
                </p>
              ) : Object.keys(leaveBalances).length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 font-medium">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading balances...
                </div>
              ) : (
                Object.entries(leaveBalances).map(([type, data]) => {
                  const isSelectedType = type === leaveType;
                  const projectedRemaining = isSelectedType ? data.remaining - totalHours : data.remaining;
                  const projectedUsed = isSelectedType ? data.used + totalHours : data.used;

                  let colorClass = "bg-green-500";
                  let bgColorClass = "bg-green-100";
                  if (type === "Personal Leave") {
                    colorClass = "bg-blue-500";
                    bgColorClass = "bg-blue-100";
                  } else if (type === "Annual Leave") {
                    colorClass = "bg-green-500";
                    bgColorClass = "bg-green-100";
                  }

                  return (
                    <div key={type}>
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className={cn("text-gray-600 font-medium", isSelectedType && "text-gray-900 font-bold")}>{getLeaveTypeLabel(type)}</span>
                        <div className="flex flex-col items-end">
                          {isSelectedType && totalHours > 0 ? (
                            <div className="flex flex-col items-end">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                {data.remaining.toFixed(4)} - {totalHours.toFixed(2)}
                              </span>
                              <span className="font-bold text-red-600">
                                = {projectedRemaining.toFixed(4)} hours
                              </span>
                            </div>
                          ) : (
                            <span className="font-bold text-gray-900">
                              {data.remaining.toFixed(4)} hours
                            </span>
                          )}
                        </div>
                      </div>
                      <Progress
                        value={(projectedUsed / (data.quota || 1)) * 100}
                        className={cn("h-2", bgColorClass)}
                        indicatorClassName={cn(colorClass, isSelectedType && totalHours > 0 && "opacity-80")}
                      />
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Insufficient Balance Disclaimer Dialog */}
      <Dialog open={showInsufficientBalanceDialog} onOpenChange={setShowInsufficientBalanceDialog}>
        <DialogContent className="rounded-[24px] border-gray-200 max-w-md">
          <DialogHeader>
            <div className="mx-auto sm:mx-0 h-12 w-12 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mb-2">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900">Insufficient Leave Balance</DialogTitle>
            <DialogDescription className="text-gray-500 text-sm">
              Unable to report leave request type due to insufficient balance. Please discuss with Management & Finance or submit Leave Without Pay.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setShowInsufficientBalanceDialog(false)}
              className="rounded-xl font-medium px-5 bg-black text-white hover:bg-gray-800 w-full sm:w-auto"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportAbsence;
